/**
 * The sentence of an Advanced (BI) query (CU-869fmk5ge, piece 2, D2): the one writer of what the screen says above the
 * results and of the first row of the Excel, so both say the same words (T2).
 *
 * `buildSentence({ form, labels, stat, selectedOffice, choose, isOfficeGroup })` → segments
 * `[{ key, before, pieces: [{ t, kind?: 'strong' | 'muted' | 'gap', list?: { noun, items } }] }]`, in the order of
 * CA 9; `sentenceText(segments)` → the plain text. `form` is BI's form (`BIFormData`), `labels` = `{ field: { id: name } }`
 * from the options the filters loaded. A name that is not known is counted ("2 insurances"); an id is never printed.
 * The capture dates are the form's, which already carries the dates queried (today when the field is empty, TL N2).
 *
 * Imports only alias-free modules: `node --test` runs it.
 */
import { fmtRange, fmtWeekday, fromIso } from '../../../Dashboards/@services/dates.js'
import { groupingPhrase } from './grouping.js'

// More values than this are said as a count, with the list behind it (CA 12)
export const MANY = 3

const STAT = { min: 'Lowest', avg: 'Average', max: 'Highest' }

const strong = t => ({ t, kind: 'strong' })
const muted = t => ({ t, kind: 'muted' })
const gap = t => ({ t, kind: 'gap' })
const plain = t => ({ t })

const asList = v => (v === null || v === undefined || v === '' ? [] : Array.isArray(v) ? v : [v])
const and = items => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`)
const plural = (n, [one, many]) => `${n} ${n === 1 ? one : many}`

/** Names of `ids` from the registry, or `null` when any is unknown */
function names(labels, field, ids) {
	const map = labels?.[field] || {}
	const out = ids.map(id => map[String(id)])
	return out.every(n => typeof n === 'string' && n) ? out : null
}

/** One piece for a list of values: the names (≤ MANY), a count with the list (> MANY), or a bare count (names unknown) */
function valuesPiece(labels, field, ids, nouns) {
	const known = names(labels, field, ids)
	if (!known) return strong(plural(ids.length, nouns))
	if (known.length > MANY) return { t: plural(known.length, nouns), kind: 'strong', list: { noun: nouns[1], items: known } }
	return strong(and(known))
}

function pricePieces(form, stat) {
	const price = form.isDailyPrice ? 'daily price' : 'total price'
	if (STAT[stat]) return [strong(`${STAT[stat]} ${price}`)]
	return [strong(price[0].toUpperCase() + price.slice(1)), muted(' (lowest, average and highest)')]
}

function lorPieces(form) {
	const lor = asList(form.lor).map(String)
	if (!lor.length) return [plain('for '), gap('rentals of which length?')]
	if (lor.length > MANY) return [plain('for '), { t: plural(lor.length, ['rental length', 'rental lengths']), kind: 'strong', list: { noun: 'rental lengths', items: lor.map(l => `${l} days`) } }]
	return [plain('for '), strong(`${and(lor)}-day`), plain(' rentals')]
}

function groupPieces(form) {
	const phrase = groupingPhrase(form.reportType, form.groupBy)
	return [phrase ? strong(phrase) : gap('grouped how?')]
}

function officePieces(form, labels, selectedOffice, isOfficeGroup) {
	const groups = asList(form.officeGroupID)
	const offices = asList(form.officePickupID)
	const out = []
	if (groups.length && (isOfficeGroup !== false || !offices.length)) {
		out.push(plain('at office group '), valuesPiece(labels, 'officeGroup', groups, ['office group', 'office groups']))
	} else if (!offices.length) {
		out.push(plain('at '), gap(isOfficeGroup ? 'which office group?' : 'which office?'))
	} else if (selectedOffice !== null && selectedOffice !== undefined && offices.map(String).includes(String(selectedOffice)) && offices.length > 1) {
		const one = names(labels, 'office', [selectedOffice])
		out.push(plain('at '), strong(one ? one[0] : 'one office'), muted(` (1 of ${offices.length} offices)`))
	} else {
		out.push(plain('at '), valuesPiece(labels, 'office', offices, ['office', 'offices']))
	}
	const dropoff = asList(form.officeDropoffID)
	if (dropoff.length) out.push(plain(', returned at '), valuesPiece(labels, 'office', dropoff, ['office', 'offices']))
	return out
}

function datesPieces(start, end, { one, many, missing }) {
	const a = fromIso(start), z = fromIso(end)
	if (!a || !z) return [plain(`${one} `), gap(missing)]
	if (+a === +z) return [plain(`${one} on `), strong(fmtWeekday(a))]
	return [plain(`${many} `), strong(fmtRange(a, z))]
}

function comparePieces(form) {
	if (!form.isPreviousComparison) return null
	const q = parseInt(form.periodComp?.quantity, 10)
	if (!q) return [plain('compared with '), gap('how long before?')]
	if (form.periodComp?.type === 'year') return [plain('compared with '), strong(q === 1 ? 'the same dates last year' : `the same dates ${q} years before`)]
	return [plain('compared with '), strong(q === 1 ? 'the day before' : `${q} days before`)]
}

// The yes/no features of "Show features" (webback master_data/service.py:208-217): one value = a filter, both = none
const FLAGS = [
	['unlimited_km', 'mileage', { 1: 'unlimited mileage', 0: 'limited mileage' }],
	['automatic', 'transmission', { 1: 'automatic', 0: 'manual' }],
	['in_terminal_pickup', 'pick-up point', { 1: 'in-terminal pick-up', 0: 'off-terminal pick-up' }],
	['is_prepaid', 'payment method', { 1: 'prepaid', 0: 'not prepaid' }],
]

function filterPieces(form, labels, choose) {
	const parts = []
	const listed = (label, field, ids, nouns) => [plain(`${label}: `), valuesPiece(labels, field, ids, nouns)]

	const siteGroups = asList(form.siteGroupID), sites = asList(form.siteID)
	if (siteGroups.length) parts.push(listed(siteGroups.length > 1 ? 'site groups' : 'site group', 'siteGroup', siteGroups, ['site group', 'site groups']))
	else if (sites.length) parts.push(listed(sites.length > 1 ? 'sites' : 'site', 'site', sites, ['site', 'sites']))
	else if (choose?.includes('siteID')) parts.push([gap('which site?')])
	else parts.push([muted('all sites')])

	const providers = asList(form.providerID)
	parts.push(providers.length ? listed(providers.length > 1 ? 'providers' : 'provider', 'provider', providers, ['provider', 'providers']) : [muted('all providers')])

	const optional = [
		['tiers', 'tier', 'tier', ['tier', 'tiers']],
		['webSupplierID', 'webSupplier', 'web supplier', ['web supplier', 'web suppliers']],
		['categoryID', 'category', 'category', ['category', 'categories']],
	]
	for (const [key, field, label, nouns] of optional) {
		const ids = asList(form[key])
		if (ids.length) parts.push(listed(ids.length > 1 ? nouns[1] : label, field, ids, nouns))
	}
	const acriss = asList(form.acriss).map(String)
	if (acriss.length) parts.push([plain('ACRISS: '), acriss.length > MANY ? { t: plural(acriss.length, ['ACRISS code', 'ACRISS codes']), kind: 'strong', list: { noun: 'ACRISS codes', items: acriss } } : strong(and(acriss))])

	for (const [key, label, words] of FLAGS) {
		const values = [...new Set(asList(form[key]).map(Number))]
		if (values.length !== 1) continue
		// a value the server does not offer today (payment method 2 is commented out) is said, never printed
		parts.push([strong(words[values[0]] || `a filtered ${label}`)])
	}
	const fuel = asList(form.fuelPolicyID)
	if (fuel.length) parts.push(listed('fuel policy', 'fuelPolicy', fuel, ['fuel policy', 'fuel policies']))
	const insurance = asList(form.insurance)
	if (insurance.length) parts.push(listed(insurance.length > 1 ? 'insurances' : 'insurance', 'insurance', insurance, ['insurance', 'insurances']))

	const currency = asList(form.currency)
	const code = currency.length ? names(labels, 'currency', currency) : null
	parts.push(!currency.length ? [plain('in '), gap('which currency?')] : [plain('in '), strong(code ? code[0] : 'the chosen currency')])

	// "Site: …" starts a sentence; a filter whose name is unknown says only its count ("1 fuel policy")
	const pieces = parts.flatMap((p, i) => (i ? [muted(' · '), ...p] : p)).map(p => ({ ...p }))
	pieces[0].t = pieces[0].t[0].toUpperCase() + pieces[0].t.slice(1)
	return pieces
}

export function buildSentence({ form = {}, labels = {}, stat = null, selectedOffice = null, choose = [], isOfficeGroup } = {}) {
	const segments = [
		{ key: 'price', before: '', pieces: pricePieces(form, stat) },
		{ key: 'lor', before: ' ', pieces: lorPieces(form) },
		{ key: 'group', before: ', ', pieces: groupPieces(form) },
		{ key: 'offices', before: ', ', pieces: officePieces(form, labels, selectedOffice, isOfficeGroup) },
		{ key: 'pickup', before: ', ', pieces: datesPieces(form.startPickupDate, form.endPickupDate, { one: 'pick-up', many: 'pick-ups', missing: 'on which date?' }) },
		{ key: 'booking', before: ', ', pieces: datesPieces(form.startBookingDate, form.endBookingDate, { one: 'prices captured', many: 'prices captured', missing: 'when?' }) },
	]
	const compare = comparePieces(form)
	if (compare) segments.push({ key: 'compare', before: ', ', pieces: compare })
	segments.push({ key: 'filters', before: '. ', pieces: [...filterPieces(form, labels, choose), plain('.')] })
	return segments
}

const segmentText = s => s.pieces.map(p => p.t).join('')

/** The plain text of a sentence: what the Excel writes as its first row */
export const sentenceText = segments => segments.map(s => `${s.before}${segmentText(s)}`).join('')

/** The keys whose words differ between two sentences (the highlight of "See the new question") */
export function changedKeys(was, now) {
	const before = Object.fromEntries(was.map(s => [s.key, segmentText(s)]))
	return now.filter(s => before[s.key] !== segmentText(s)).map(s => s.key)
		.concat(was.filter(s => !now.some(n => n.key === s.key)).map(s => s.key))
}


/**
 * The first row of the Excel of what the screen shows (CA 13): the same words, `undefined` when nothing was answered.
 * `allOffices`: "Download all offices excel" walks every office (webback drops `officePickupIDAux`), so its sentence
 * never says "1 of N offices" (TL C2).
 */
export function excelSentence(asked, stat, { allOffices = false } = {}) {
	if (!asked || asked.phase !== 'done') return undefined
	return sentenceText(buildSentence({ form: asked.form, labels: asked.labels, stat, selectedOffice: allOffices ? null : asked.selectedOffice }))
}
